import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AddVersionForm } from '../components/AddVersionForm';
import { EditVersionModal } from '../components/EditVersionModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
// import DefaultLayout from '../layout/DefaultLayout';

interface ArchivedPricingTableVersion {
  id: string;
  pricing_table_id: string;
  version_number: number;
  data: any;
  created_at: Date;
}

interface PricingTableWithVersions {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  template_id: string | null;
  selected_purpose: string;
  currency: string;
  form_state: any | null;
  form_progress: any | null;
  created_at: Date;
  updated_at: Date;
  abtest_features_options: any | null;
  abtest_pricing_options: any | null;
  abtest_tier_inclusion_options: any | null;
  is_uploaded: boolean;
  archived_pricing_table_versions: ArchivedPricingTableVersion[];
}

export function ViewPricingTablePage() {
  const { pricingTableId } = useParams<{ pricingTableId: string }>();
  const [tableWithVersions, setTableWithVersions] = useState<PricingTableWithVersions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<ArchivedPricingTableVersion | null>(null);

  const fetchPricingTableWithVersions = async () => {
    if (!pricingTableId) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/pricing-tables/${pricingTableId}/versions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load pricing table');
      }

      const result = await response.json();
      setTableWithVersions(result.data);
    } catch (error: any) {
      setError(error.message || 'Error loading pricing table');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingTableWithVersions();
  }, [pricingTableId]);

  const handleDeleteVersion = async (versionId: string) => {
    if (window.confirm('Are you sure you want to delete this version?')) {
      try {
        const response = await fetch(`/api/pricing-tables/versions/${versionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete version');
        }

        alert('Version deleted successfully');
        fetchPricingTableWithVersions(); // Refresh the data
      } catch (error: any) {
        console.error('Error deleting version:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  if (isLoading) return (
    <div className="p-4">Loading pricing table details...</div>
  );

  if (error) return (
    <div className="p-4 text-red-500">Error loading pricing table: {error}</div>
  );

  if (!tableWithVersions) {
    return (
      <div className="p-4">Loading...</div>
    );
  }

  const table = tableWithVersions;

  return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{table.name}</h1>
          <p className="text-gray-600 mb-4">{table.description}</p>
          <div className="flex gap-4 text-sm text-gray-500">
            <span>Created: {formatDate(table.created_at)}</span>
            <span>Status: {table.is_active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>

        <div className="grid gap-6">
          {table.archived_pricing_table_versions && table.archived_pricing_table_versions.length > 0 ? (
            table.archived_pricing_table_versions.map((version) => {
              const data = version.data as any;
              return (
                <Card key={version.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{data.version_name}</h3>
                      <p className="text-2xl font-bold text-green-600">${data.price}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingVersion(version)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteVersion(version.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{data.product_description}</p>
                  <div className="text-sm text-gray-500">
                    Version {version.version_number} • Created {formatDate(version.created_at)}
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-6 text-center text-gray-500">
              No versions found for this pricing table.
            </Card>
          )}
        </div>

        <div className="mt-8">
          <AddVersionForm
            pricingTableId={table.id}
            onVersionAdded={fetchPricingTableWithVersions}
          />
        </div>

        {editingVersion && (
          <EditVersionModal
            version={editingVersion}
            isOpen={!!editingVersion}
            onOpenChange={(open) => !open && setEditingVersion(null)}
            onVersionUpdated={() => {
              setEditingVersion(null);
              fetchPricingTableWithVersions();
            }}
          />
        )}
      </div>
  );
}
